import { useContext } from "react";
import { Unstable_Grid2 as Grid, Box } from "@mui/material";
import { RecordContext } from "../../components/record/Record";
import { formatGroups, Group } from "../../utils/formatGroups";
import { GroupsOptions } from "../../types/groups.types";
import { DynamicTasksGroupBlock } from "./DynamicTasksGroupBlock";
import { AttributionColumn } from "./AttributionColumn";
import { TriggerSummary } from "./TriggerSummary";
import { attributionFields } from "./dynamicTasksOptions";

type DynamicTasksGroupsLayoutProps = {
	groupsOptions?: GroupsOptions;
	bottomPadding?: number;
};

// Custom 3-column arrangement for dynamic_tasks: [ group1 | group2 over group3 | group4 ].
// Follows the SessionPreview pattern (format the groups here and place them manually) so the
// shared Groups component stays untouched. Groups are taken in schema order; extra groups, if any,
// render full-width below so nothing is dropped.
export const DynamicTasksGroupsLayout = ({ groupsOptions, bottomPadding = 100 }: DynamicTasksGroupsLayoutProps) => {
	const context = useContext(RecordContext);
	const item = context?.item;
	const fields = context?.formatedFields;
	const entityForm = context?.entityForm;
	const type = context?.type ?? "detail";

	if (!fields) return null;

	const { groups } = formatGroups(fields, item, type, groupsOptions?.fieldsWithExtraOptions);
	const visibleGroups = groups.filter((group) => !group.hidden);

	const [firstGroup, secondGroup, thirdGroup, fourthGroup, ...restGroups] = visibleGroups;

	const contentField = fields.find((field) => field.field === "content");

	// content is rendered on its own at the end of the attribution column (not in its schema group).
	const renderContentBlock = () => {
		if (!contentField) return null;
		const contentGroup: Group = { groupId: "__content", groupName: "", fields: [contentField] };
		return (
			<Box sx={{ mt: 2 }}>
				<DynamicTasksGroupBlock group={contentGroup} item={item} type={type} entityForm={entityForm} groupsOptions={{ fieldsWithExtraOptions: [] }} hideTitle />
			</Box>
		);
	};

	const renderBlock = (group?: Group) => {
		if (!group) return null;
		const isAttributionGroup = group.fields.some((field) => attributionFields.includes(field.field));
		if (isAttributionGroup) {
			return (
				<>
					{type !== "detail" ? (
						<AttributionColumn group={group} item={item} type={type} entityForm={entityForm} groupsOptions={groupsOptions} />
					) : (
						<DynamicTasksGroupBlock group={group} item={item} type={type} entityForm={entityForm} groupsOptions={groupsOptions} />
					)}
					{renderContentBlock()}
				</>
			);
		}
		return <DynamicTasksGroupBlock group={group} item={item} type={type} entityForm={entityForm} groupsOptions={groupsOptions} />;
	};

	return (
		<Grid
			container
			justifyContent="flex-start"
			alignItems="flex-start"
			sx={{ backgroundColor: "#fff", paddingBottom: `${bottomPadding}px` }}
		>
			<Grid xs={12}>
				<TriggerSummary />
			</Grid>
			<Grid xs={12} lg={4} p={4} minWidth={300}>
				{renderBlock(firstGroup)}
			</Grid>
			<Grid xs={12} lg={4} p={4} minWidth={300}>
				{renderBlock(secondGroup)}
				{thirdGroup && <Box sx={{ mt: 4 }}>{renderBlock(thirdGroup)}</Box>}
			</Grid>
			<Grid xs={12} lg={4} p={4} minWidth={300}>
				{renderBlock(fourthGroup)}
			</Grid>
			{restGroups.length > 0 && (
				<Grid xs={12} p={4} minWidth={300}>
					{restGroups.map((group) => (
						<Box key={group.groupId} sx={{ mt: 4 }}>
							{renderBlock(group)}
						</Box>
					))}
				</Grid>
			)}
		</Grid>
	);
};
